// Rótulos, cores e formatações compartilhados pela Fila de Solicitações (suporte) e pelo sino de notificações (solicitante).
import { postUpload } from "utils/WebService";
import { TEXTO_COLORIDO_ESCURO } from "assets/coresTema";

/**
 * Pedido/devolução/migração com os anexos na MESMA chamada (multipart): o backend grava a ocorrência e os arquivos
 * (aba Documentos da ficha, ligados à ocorrência) juntos. Se um arquivo não puder ser gravado, nada é feito e a
 * mensagem do erro volta para a tela. campos: { observacao, contratosIds: [...], dados: "json" ... }.
 */
export function enviarComAnexos(url, campos, arquivos) {
    const formData = new FormData();
    Object.entries(campos || {}).forEach(([nome, valor]) => {
        if (valor === null || valor === undefined) return;
        if (Array.isArray(valor)) valor.forEach(v => formData.append(nome, v));
        else formData.append(nome, valor);
    });
    (Array.isArray(arquivos) ? arquivos : [arquivos]).filter(Boolean).forEach(arquivo => formData.append("arquivos", arquivo));
    return postUpload(url, formData);
}

export const TIPOS_SOLICITACAO = {
    DEVOLUCAO_FICHA: { label: "Devolução de ficha", icone: "assignment_return", cor: "#AD1457", corEscura: "#F06292" },
    DEVOLUCAO_CONTRATO: { label: "Devolução de contrato", icone: "receipt_long", cor: "#6A1B9A", corEscura: TEXTO_COLORIDO_ESCURO.roxo },
    MIGRACAO_CARTEIRA: { label: "Migração de carteira", icone: "swap_horiz", cor: "#1565C0", corEscura: "#64B5F6" },
    // Pedidos da tela Solicitações ao Suporte (o nome que aparece é o do tipo da lista do suporte, nomeTipo)
    REATIVACAO_FICHA: { label: "Reativação de ficha", icone: "restore", cor: "#2E7D32", corEscura: "#81C784" },
    REATIVACAO_CONTRATO: { label: "Reativação de débito", icone: "restore_page", cor: "#2E7D32", corEscura: "#81C784" },
    MANUAL: { label: "Solicitação ao suporte", icone: "support_agent", cor: "#B26A00", corEscura: "#FFB74D" },
};

/** O tipo da solicitação: o nome da lista do suporte (nomeTipo) com o ícone e a cor da ação. */
export const tipoDaSolicitacao = (s) => {
    const base = tipoSolicitacao(s?.tipoSolicitacao);
    return s?.nomeTipo ? { ...base, label: s.nomeTipo } : base;
};

/** O pedido manual (baixa de pagamento, saldo, outros): o suporte conclui com a resposta. */
export const ehPedidoManual = (s) => s?.tipoSolicitacao === "MANUAL";

export const tipoSolicitacao = (codigo) =>
    TIPOS_SOLICITACAO[codigo] || { label: codigo || "-", icone: "help_outline", cor: "#7b809a", corEscura: "#c3c2b7" };

// Linha do tempo da solicitação (tbSolicitacaoSuporteHistorico.ccAcao)
export const ACOES_HISTORICO = {
    SOLICITADA: { label: "Solicitada", icone: "send", cor: "info" },
    RECUSADA: { label: "Recusada pelo suporte", icone: "block", cor: "error" },
    COBRADA: { label: "Solicitante cobrado", icone: "notifications_active", cor: "warning" },
    REENVIADA: { label: "Reenviada pelo solicitante", icone: "replay", cor: "info" },
    CIENTE: { label: "Solicitante ciente da recusa", icone: "done_all", cor: "secondary" },
    ENCERRADA: { label: "Encerrada pelo suporte", icone: "lock", cor: "secondary" },
    APROVADA: { label: "Aprovada / concluída", icone: "check_circle", cor: "success" },
    ANEXO_INCLUIDO: { label: "Anexo incluído pelo solicitante", icone: "attach_file", cor: "info" },
    ANEXO_TROCADO: { label: "Anexo trocado pelo solicitante", icone: "swap_horiz", cor: "warning" },
    ANEXO_EXCLUIDO: { label: "Anexo excluído pelo solicitante", icone: "delete_outline", cor: "secondary" },
    ATENDIMENTO_INICIADO: { label: "Atendimento iniciado", icone: "play_circle", cor: "warning" },
    ATENDIMENTO_ASSUMIDO: { label: "Atendimento assumido", icone: "swap_horiz", cor: "warning" },
    ANDAMENTO: { label: "Andamento do suporte", icone: "chat", cor: "info" },
    // Migração para o Jurídico (26/10/2026): o pedido passa primeiro pelo CobJur (Migrações para o Jurídico)
    JURIDICO_ENVIOU: { label: "Jurídico preencheu e enviou ao suporte", icone: "gavel", cor: "info" },
    JURIDICO_RECUSOU: { label: "Recusada pelo Jurídico", icone: "block", cor: "error" },
    DEVOLVIDA_AO_JURIDICO: { label: "Devolvida pelo suporte ao Jurídico", icone: "undo", cor: "warning" },
};

/**
 * Etapas dos cards (Solicitações ao Suporte), na ordem em que a solicitação anda; vem pronta do backend (etapa).
 * cor = cor do tema (info, warning, error, success, secondary).
 */
export const ETAPAS = {
    SOLICITADA: { label: "Solicitado", icone: "send", cor: "info", ajuda: "O suporte ainda não começou." },
    EM_ANDAMENTO: { label: "Em andamento", icone: "engineering", cor: "warning", ajuda: "O suporte (ou o Jurídico, na migração) está cuidando." },
    AGUARDANDO_VOCE: { label: "Aguardando você", icone: "report", cor: "error", ajuda: "Recusada: dê ciência ou reenvie." },
    CONCLUIDA: { label: "Concluído", icone: "task_alt", cor: "success", ajuda: "Aprovadas, concluídas e recusas encerradas." },
    ENCERRADA: { label: "Encerrada", icone: "lock", cor: "secondary", ajuda: "" },
};

/** A etapa da solicitação (a do backend; sem ela, pelo status). */
export function etapaDaSolicitacao(s) {
    if (s?.etapa) return s.etapa;
    if (s?.status === "JURIDICO") return "EM_ANDAMENTO";
    if (s?.status === "PENDENTE") return "SOLICITADA";
    if (s?.status === "REJEITADA") return "AGUARDANDO_VOCE";
    return s?.status === "APROVADA" ? "CONCLUIDA" : "ENCERRADA";
}

// Avisa as telas abertas (ex.: o aviso no topo da ficha) que uma solicitação foi criada ou mudou
export const EVENTO_SOLICITACOES_ALTERADAS = "ral:solicitacoes-alteradas";
export const avisarSolicitacoesAlteradas = () => window.dispatchEvent(new Event(EVENTO_SOLICITACOES_ALTERADAS));

// Pede ao sino (SystemWrapper) para ler as notificações de novo (ex.: quem pediu abriu um retorno do suporte)
export const EVENTO_ATUALIZAR_NOTIFICACOES = "ral:atualizar-notificacoes";
export const atualizarNotificacoes = () => window.dispatchEvent(new Event(EVENTO_ATUALIZAR_NOTIFICACOES));

export const acaoHistorico = (codigo) => ACOES_HISTORICO[codigo] || { label: codigo, icone: "circle", cor: "secondary" };

const HORA = 3600000;
const DIA = 24 * HORA;

/** Tempo decorrido em texto curto: "agora", "35 min", "5h", "2d 4h", "45d". */
export function formatarDuracao(ms) {
    if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "-";
    if (ms < 60000) return "agora";
    if (ms < HORA) return `${Math.floor(ms / 60000)} min`;
    if (ms < DIA) return `${Math.floor(ms / HORA)}h`;
    const dias = Math.floor(ms / DIA);
    const horas = Math.floor((ms % DIA) / HORA);
    return dias >= 10 || horas === 0 ? `${dias}d` : `${dias}d ${horas}h`;
}

// Idade na fila: até 1 dia, até 3 dias, mais de 3 dias (o texto do tempo acompanha a cor)
const CORES_IDADE = {
    recente: { light: "#2E7D32", dark: "#66BB6A" },
    atencao: { light: "#B26A00", dark: "#FFB74D" },
    atrasada: { light: "#C62828", dark: "#EF5350" },
};

export function corIdade(ms, darkMode) {
    const faixa = ms < DIA ? "recente" : ms < 3 * DIA ? "atencao" : "atrasada";
    return CORES_IDADE[faixa][darkMode ? "dark" : "light"];
}

export const listaDe = (res) => res?.list || res?.data || (Array.isArray(res) ? res : []);

/** "#RRGGBB" -> "rgba(r, g, b, alfa)" */
export function comAlfa(hex, alfa) {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(valor.substring(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alfa})`;
}

/** A ficha da solicitação (nº da tbDevedorCarteira, como no cabeçalho da ficha) e a carteira dela. */
export function fichaDaSolicitacao(s) {
    if (!s.numeroFicha) return s.nomeCarteira || "";
    return `Ficha #${s.numeroFicha}${s.nomeCarteira ? ` · ${s.nomeCarteira}` : ""}`;
}

/** O que a solicitação pede, em uma linha: contrato, carteira de destino ou a ficha inteira, sempre com a ficha. */
export function alvoSolicitacao(s) {
    const ficha = fichaDaSolicitacao(s);
    if (s.tipoSolicitacao === "MIGRACAO_CARTEIRA") {
        // Só parte dos contratos da ficha: lista os títulos pedidos
        return `${ficha ? `${ficha} · ` : ""}Para: ${s.nomeCarteiraDestino || "carteira não informada"}${s.contratosMigracao ? ` · Contratos: ${s.contratosMigracao}` : ""}${s.resumoPacote ? " · com dados do Jurídico" : ""}`;
    }
    if (s.tipoSolicitacao === "DEVOLUCAO_CONTRATO") return `Contrato ${s.tituloContrato || "-"}${ficha ? ` · ${ficha}` : ""}`;
    if (s.tipoSolicitacao === "REATIVACAO_FICHA") return ficha ? `${ficha} (reativar)` : "Reativar a ficha";
    if (s.tipoSolicitacao === "REATIVACAO_CONTRATO" || s.tipoSolicitacao === "MANUAL") {
        return `${ficha || "Ficha"}${s.contratosMigracao ? ` · Débitos: ${s.contratosMigracao}` : ""}`;
    }
    return ficha ? `${ficha} (inteira)` : "Ficha inteira";
}

/** Busca livre: devedor, CPF/CNPJ (com ou sem pontuação), cliente, contrato, solicitante, ficha e carteira. */
export function correspondeBusca(s, termo) {
    if (!termo) return true;
    const t = termo.trim().toLowerCase();
    if (!t) return true;
    const soDigitos = t.replace(/\D/g, "");
    const campos = [s.nomeDevedor, s.nomeCliente, s.tituloContrato, s.nomeSolicitante, s.nomeCarteiraDestino, s.cpfCnpjDevedor,
        s.nomeCarteira, s.numeroFicha, s.nomeTipo, s.observacao];
    if (campos.some((c) => c && String(c).toLowerCase().includes(t))) return true;
    return soDigitos.length >= 3 && String(s.cpfCnpjDevedor || "").replace(/\D/g, "").includes(soDigitos);
}
